import React, { useState, useEffect } from "react";
import { MdAdd, MdDelete, MdUpload, MdLink, MdCalendarToday, MdPerson, MdTitle, MdDescription, MdArrowBack, MdArrowForward, MdCheck } from "react-icons/md";
import documentService from "service/document.service";

const DocumentEditModal = ({ onClose, documentId, articleId, onSuccess, existingDocument }) => {
  const [formData, setFormData] = useState({
    title_doc: "",
    auth_doc: "",
    type_doc: "Annexe",
    source_doc: {
      urls: ["", ""],
      date: "",
    },
    path_doc: null,
    keepExistingFile: true, // Flag pour garder le fichier existant
  });

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Types de documents possibles
  const documentTypes = [
    "Annexe",
    "Référence",
    "Illustration",
    "Carte",
    "Tableau",
    "Graphique",
    "Protocole",
    "Autre",
  ];

  // Charger les données du document existant
  useEffect(() => {
    const loadDocumentData = async () => {
      try {
        setInitialLoading(true);
        setError(null);

        let documentData = existingDocument;

        // Si les données ne sont pas fournies, les récupérer via l'API
        if (!documentData && documentId) {
          const response = await documentService.getDocumentById(documentId);
          documentData = response.data;
        }

        if (documentData) {
          // Transformer les URLs de l'API (url1, url2, etc.) en tableau
          const urlsArray = [];
          let urlIndex = 1;
          while (documentData.source_doc?.[`url${urlIndex}`]) {
            urlsArray.push(documentData.source_doc[`url${urlIndex}`]);
            urlIndex++;
          }
          
          // S'assurer qu'il y a au moins 2 champs URL
          if (urlsArray.length === 0) {
            urlsArray.push("", "");
          } else if (urlsArray.length === 1) {
            urlsArray.push("");
          }

          setFormData({
            title_doc: documentData.title_doc || "",
            auth_doc: documentData.auth_doc || "",
            type_doc: documentData.type_doc || "Annexe",
            source_doc: {
              urls: urlsArray,
              date: documentData.source_doc?.date || "",
            },
            path_doc: null,
            keepExistingFile: true,
            existingFileName: documentData.path_doc ? documentData.path_doc.split('/').pop() : null,
          });
        }
      } catch (err) {
        console.error("Erreur lors du chargement du document:", err);
        setError("Impossible de charger les données du document");
      } finally {
        setInitialLoading(false);
      }
    };

    loadDocumentData();
  }, [documentId, existingDocument]);

  // Détecter les changements non sauvegardés
  useEffect(() => {
    if (!initialLoading && existingDocument) {
      const hasChanges = 
        formData.title_doc !== (existingDocument.title_doc || "") ||
        formData.auth_doc !== (existingDocument.auth_doc || "") ||
        formData.type_doc !== (existingDocument.type_doc || "Annexe") ||
        formData.source_doc.date !== (existingDocument.source_doc?.date || "") ||
        !formData.keepExistingFile ||
        formData.path_doc !== null;
      
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, existingDocument, initialLoading]);

  // Gérer les changements dans les champs du formulaire
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Gérer le changement d'URL à un index spécifique
  const handleUrlChange = (index, value) => {
    const newUrls = [...formData.source_doc.urls];
    newUrls[index] = value;
    
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        urls: newUrls
      }
    });
  };

  // Ajouter un nouveau champ URL
  const addUrlField = () => {
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        urls: [...formData.source_doc.urls, ""]
      }
    });
  };

  // Supprimer un champ URL
  const removeUrlField = (index) => {
    if (formData.source_doc.urls.length <= 1) return;
    
    const newUrls = [...formData.source_doc.urls];
    newUrls.splice(index, 1);
    
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        urls: newUrls
      }
    });
  };

  // Gérer la date de source
  const handleDateChange = (value) => {
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        date: value
      }
    });
  };

  // Gérer le changement de fichier
  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setFormData({
      ...formData,
      path_doc: file,
      keepExistingFile: file === null,
    });
  };

  // Supprimer le nouveau fichier sélectionné
  const removeNewFile = () => {
    setFormData({
      ...formData,
      path_doc: null,
      keepExistingFile: true,
    });
  };

  // Navigation entre les étapes
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Vérification si l'étape actuelle est valide
  const isStepValid = () => {
    if (currentStep === 1) {
      return !!formData.title_doc;
    }
    return true;
  };

  // Gérer la fermeture avec confirmation si nécessaire
  const handleCloseWithConfirm = () => {
    if (loading || initialLoading) return;

    if (hasUnsavedChanges) {
      if (
        window.confirm(
          "Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter?"
        )
      ) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation finale
    if (!formData.title_doc) {
      setError("Le titre du document est obligatoire");
      setCurrentStep(1);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Conversion du format des URLs pour l'API
      const apiSourceDoc = {
        ...formData.source_doc,
        // Transformer le tableau d'URLs en objet avec clés url1, url2, url3, etc.
        ...formData.source_doc.urls.reduce((acc, url, index) => {
          if (url) {
            acc[`url${index + 1}`] = url;
          }
          return acc;
        }, {})
      };
      
      // Supprimer le tableau urls qui n'est pas nécessaire pour l'API
      delete apiSourceDoc.urls;

      // Préparation des données pour l'API
      const submitData = new FormData();

      // Ajouter tous les champs au FormData
      submitData.append("title_doc", formData.title_doc);
      submitData.append("auth_doc", formData.auth_doc);
      submitData.append("type_doc", formData.type_doc);
      submitData.append("source_doc", JSON.stringify(apiSourceDoc));
      submitData.append("id_art", articleId);

      // Ajouter le fichier seulement si un nouveau fichier a été sélectionné
      if (formData.path_doc && !formData.keepExistingFile) {
        submitData.append("path_doc", formData.path_doc);
      }

      // Appel à l'API pour la mise à jour
      const response = await documentService.updateDocument(documentId, submitData);
      console.log("Document mis à jour avec succès:", response);

      setSuccess("Le document a été mis à jour avec succès!");

      // Attendre un peu avant de fermer la modal
      setTimeout(() => {
        onSuccess && onSuccess(response);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Erreur lors de la mise à jour du document:", err);
      setError("Échec de la mise à jour du document. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Affichage des indicateurs d'étape
  const renderStepIndicators = () => {
    return (
      <div className="mb-8 flex items-center justify-center">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className="flex items-center">
            <div 
              className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                currentStep > i + 1 
                  ? "border-[#57A863] bg-[#57A863] text-white" 
                  : currentStep === i + 1 
                    ? "border-[#57A863] text-[#57A863]" 
                    : "border-gray-300 text-gray-300"
              }`}
            >
              {currentStep > i + 1 ? <MdCheck className="h-6 w-6" /> : i + 1}
            </div>
            
            {i < totalSteps - 1 && (
              <div className={`h-1 w-16 ${
                currentStep > i + 1 ? "bg-[#57A863]" : "bg-gray-300"
              }`} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Affichage du contenu des étapes
  const renderStepContent = () => {
    if (initialLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <svg
              className="h-6 w-6 animate-spin text-[#57A863]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-gray-600">Chargement des données du document...</span>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <div className="step-content animate-fadeIn">
            <h3 className="mb-6 text-center text-lg font-medium text-gray-800">
              Informations de base
            </h3>
            
            {/* Titre du document */}
            <div className="mb-6">
              <label
                htmlFor="title_doc"
                className="flex items-center text-sm font-medium text-gray-700 mb-1"
              >
                <MdTitle className="mr-2 h-5 w-5 text-gray-500" />
                Titre du document <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="title_doc"
                value={formData.title_doc}
                onChange={(e) => handleInputChange("title_doc", e.target.value)}
                placeholder="Titre du document"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                required
              />
            </div>

            {/* Auteur du document */}
            <div className="mb-6">
              <label
                htmlFor="auth_doc"
                className="flex items-center text-sm font-medium text-gray-700 mb-1"
              >
                <MdPerson className="mr-2 h-5 w-5 text-gray-500" />
                Auteur du document
              </label>
              <input
                type="text"
                id="auth_doc"
                value={formData.auth_doc}
                onChange={(e) => handleInputChange("auth_doc", e.target.value)}
                placeholder="Nom de l'auteur"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
              />
            </div>

            {/* Type de document */}
            <div>
              <label
                htmlFor="type_doc"
                className="flex items-center text-sm font-medium text-gray-700 mb-1"
              >
                <MdDescription className="mr-2 h-5 w-5 text-gray-500" />
                Type de document
              </label>
              <select
                id="type_doc"
                value={formData.type_doc}
                onChange={(e) => handleInputChange("type_doc", e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
              >
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="step-content animate-fadeIn">
            <h3 className="mb-6 text-center text-lg font-medium text-gray-800">
              Sources
            </h3>
            
            {/* URLs dynamiques */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                <MdLink className="mr-2 h-5 w-5 text-gray-500" />
                URLs de référence
              </label>
              
              <div className="space-y-4">
                {formData.source_doc.urls.map((url, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <label htmlFor={`url${index + 1}`} className="block text-xs text-gray-600 mb-1">
                          URL {index + 1}
                        </label>
                        {formData.source_doc.urls.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeUrlField(index)}
                            className="text-gray-400 hover:text-red-500"
                            title="Supprimer cette URL"
                          >
                            <MdDelete className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        id={`url${index + 1}`}
                        value={url}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        placeholder="https://example.com"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      />
                    </div>
                  </div>
                ))}
                
                {/* Bouton pour ajouter une URL */}
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={addUrlField}
                    className="flex items-center justify-center rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 transition-colors"
                  >
                    <MdAdd className="mr-2 h-5 w-5" /> Ajouter une URL
                  </button>
                </div>
              </div>
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <MdCalendarToday className="mr-2 h-5 w-5 text-gray-500" />
                Date
              </label>
              <input
                type="date"
                id="date"
                value={formData.source_doc.date}
                onChange={(e) => handleDateChange(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content animate-fadeIn">
            <h3 className="mb-6 text-center text-lg font-medium text-gray-800">
              Fichier du document
            </h3>
            
            {/* Fichier actuel */}
            {formData.existingFileName && formData.keepExistingFile && (
              <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h4 className="mb-2 text-sm font-medium text-gray-700">Fichier actuel</h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">{formData.existingFileName}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, keepExistingFile: false})}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Remplacer
                  </button>
                </div>
              </div>
            )}

            {/* Upload de nouveau fichier */}
            {(!formData.keepExistingFile || !formData.existingFileName) && (
              <div className="mb-4">
                <div className="flex items-center justify-center">
                  <MdUpload className="mr-2 h-6 w-6 text-[#57A863]" />
                  <span className="text-sm font-medium text-gray-700">
                    {formData.existingFileName ? "Nouveau fichier" : "Fichier"}
                  </span>
                </div>
                
                <div className="mt-4 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600 justify-center">
                      <label
                        htmlFor="path_doc"
                        className="relative cursor-pointer rounded-md bg-white font-medium text-[#57A863] focus-within:outline-none focus-within:ring-2 focus-within:ring-[#57A863] focus-within:ring-offset-2 hover:text-[#4a9154]"
                      >
                        <span>Télécharger un fichier</span>
                        <input 
                          id="path_doc" 
                          name="path_doc" 
                          type="file" 
                          onChange={handleFileChange}
                          className="sr-only" 
                        />
                      </label>
                      <p className="pl-1">ou glisser-déposer</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, PNG, JPG jusqu'à 10MB
                    </p>
                  </div>
                </div>
                
                {formData.path_doc && (
                  <div className="mt-5 flex items-center justify-between rounded-md bg-[#eaf5ec] p-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 p-1">
                        <svg className="h-5 w-5 text-[#57A863]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-2 text-sm font-medium text-[#57A863]">
                        {formData.path_doc.name}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeNewFile}
                      className="text-gray-400 hover:text-red-500"
                      title="Supprimer le fichier sélectionné"
                    >
                      <MdDelete className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {formData.existingFileName && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, keepExistingFile: true, path_doc: null})}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Garder le fichier actuel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Résumé des informations */}
            <div className="mt-8 rounded-md bg-gray-50 p-4">
              <h4 className="mb-2 font-medium text-gray-700">Résumé des modifications</h4>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="text-gray-500">Titre:</span>{" "}
                  <span className="font-medium">{formData.title_doc || "(Non renseigné)"}</span>
                </p>
                <p>
                  <span className="text-gray-500">Auteur:</span>{" "}
                  <span>{formData.auth_doc || "(Non renseigné)"}</span>
                </p>
                <p>
                  <span className="text-gray-500">Type:</span>{" "}
                  <span>{formData.type_doc}</span>
                </p>
                <p>
                  <span className="text-gray-500">Date:</span>{" "}
                  <span>{formData.source_doc.date || "(Non renseignée)"}</span>
                </p>
                <p>
                  <span className="text-gray-500">URLs:</span>{" "}
                  <span>{formData.source_doc.urls.filter(u => u).join(", ") || "(Non renseignées)"}</span>
                </p>
                <p>
                  <span className="text-gray-500">Fichier:</span>{" "}
                  <span>
                    {formData.path_doc 
                      ? `Nouveau: ${formData.path_doc.name}` 
                      : formData.keepExistingFile && formData.existingFileName
                        ? `Actuel: ${formData.existingFileName}`
                        : "(Aucun fichier)"
                    }
                  </span>
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6 rounded-xl">
      {/* En-tête */}
      <div className="mb-6 border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            Modifier le document
          </h2>
          <button
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
            onClick={handleCloseWithConfirm}
            disabled={loading || initialLoading}
            aria-label="Fermer"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Document ID: <span className="font-medium text-gray-700">{documentId}</span>
        </p>
      </div>

      {/* Messages d'erreur ou de succès */}
      {error && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="flex">
            <svg className="mr-2 h-5 w-5 flex-shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <div className="flex">
            <svg className="mr-2 h-5 w-5 flex-shrink-0 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {success}
          </div>
        </div>
      )}

      {/* Indicateurs d'étape */}
      {!initialLoading && renderStepIndicators()}

      {/* Formulaire par étapes */}
      <form onSubmit={e => e.preventDefault()} className="pb-6">
        {/* Contenu dynamique selon l'étape */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>

        {/* Navigation entre les étapes */}
        {!initialLoading && (
          <div className="mt-8 flex justify-between border-t pt-6">
            <div>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
                  disabled={loading}
                >
                  <MdArrowBack className="mr-1 h-5 w-5" />
                  Précédent
                </button>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCloseWithConfirm}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
                disabled={loading}
              >
                Annuler
              </button>
              
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className={`flex items-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 ${
                    isStepValid() 
                      ? "bg-[#57A863] hover:bg-[#4a9154]" 
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                  disabled={!isStepValid() || loading}
                >
                  Suivant
                  <MdArrowForward className="ml-1 h-5 w-5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className={`flex items-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 ${
                    loading
                      ? "cursor-not-allowed bg-gray-400"
                      : "bg-[#57A863] hover:bg-[#4a9154]"
                  }`}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg
                        className="mr-2 h-4 w-4 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Mise à jour en cours...
                    </span>
                  ) : (
                    <>
                      Mettre à jour le document
                      <MdCheck className="ml-1 h-5 w-5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default DocumentEditModal;